/**
 * Check-in attempts, kept in the browser for now.
 *
 * The backend has no check-in endpoint yet (v1 integration follows), so an
 * attempt's summary — never its trials, never a person's name — is stored per
 * signed-in user in localStorage, the way the pending-job pointer is. It is
 * INTERIM storage: a cleared browser loses the history, and nothing here is
 * synchronised. Practice is never stored. Interrupted attempts are stored as
 * interrupted and never stitched.
 *
 * Comparability follows docs/beacon-timing-budget.md §4: the same browser
 * family and major version, the same platform, the same input method, the same
 * protocol and presentation versions. Anything else starts a separate history.
 */
import { localIsoDay } from '../format'

import type { Summary } from './runner'
import type { DeviceContext } from './trace'

const PREFIX = 'brainspot:checkin:'

export interface Setup {
  browser: string
  platform: string
  input: string
  refreshHz: number | null
}

export interface Attempt {
  at: string
  setup: Setup
  protocolVersion: string
  presentationVersion: string
  summary: Summary
  interrupted: boolean
  /** The recording this attempt followed, when it followed one. */
  linkedSessionId: string | null
}

/**
 * Browser family and major version, the first rule that matches winning. Each
 * rule is tried against the WHOLE user agent, in this order, because the tokens
 * pile up: Edge, Opera and Samsung Internet also carry `Chrome/`, every iOS
 * browser carries `Safari/`, and Edge on iOS carries `Version/` as well. A single
 * alternation regex returns whichever token comes first in the string, which is
 * how Edge read as Chrome and Safari as "Safari 605".
 *
 * On iOS the family is the app, not WebKit: an unknown or changed context starts
 * a separate history (docs/beacon-timing-budget.md §4), so the stricter label is
 * the safe one. Safari's major comes from `Version/`, never from `Safari/`,
 * which is the WebKit build.
 */
const BROWSER_RULES: readonly (readonly [family: string, pattern: RegExp])[] = [
  ['Edge iOS', /\bEdgiOS\/(\d+)/],
  ['Chrome iOS', /\bCriOS\/(\d+)/],
  ['Firefox iOS', /\bFxiOS\/(\d+)/],
  ['Edge', /\bEdgA?\/(\d+)/],
  ['Opera', /\bOPR\/(\d+)/],
  ['Samsung Internet', /\bSamsungBrowser\/(\d+)/],
  ['Firefox', /\bFirefox\/(\d+)/],
  // No \b: `HeadlessChrome/` keeps reading as Chrome, as it did before.
  ['Chrome', /Chrome\/(\d+)/],
  // `Version/N` together with `Safari/`, in either order.
  ['Safari', /^(?=.*\bSafari\/).*?\bVersion\/(\d+)/],
]

export function browserOf(userAgent: string): { family: string; major: string } | null {
  for (const [family, pattern] of BROWSER_RULES) {
    const major = pattern.exec(userAgent)?.[1]
    if (major !== undefined) return { family, major }
  }
  return null
}

export function setupFrom(device: DeviceContext): Setup {
  const b = browserOf(device.userAgent)
  const browser = b ? `${b.family} ${b.major}` : 'unknown browser'
  return { browser, platform: device.platform, input: device.inputMethods.join('+') || 'unknown', refreshHz: device.estimatedRefreshHz }
}

export function sameSetup(a: Setup, b: Setup): boolean {
  return a.browser === b.browser && a.platform === b.platform && a.input === b.input
}

export function comparable(a: Attempt, b: Attempt): boolean {
  return sameSetup(a.setup, b.setup) && a.protocolVersion === b.protocolVersion && a.presentationVersion === b.presentationVersion
}

function safely<T>(run: () => T, fallback: T): T {
  try {
    return run()
  } catch {
    return fallback
  }
}

export function readAttempts(userId: string): Attempt[] {
  return safely(() => {
    const raw = localStorage.getItem(PREFIX + userId)
    const parsed: unknown = raw === null ? [] : JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Attempt[]) : []
  }, [])
}

export function appendAttempt(userId: string, attempt: Attempt): void {
  safely(() => localStorage.setItem(PREFIX + userId, JSON.stringify([...readAttempts(userId), attempt])), undefined)
}

/** Every user's attempts. Called on sign-out, for the same shared-machine reason as the pending-job pointer. */
export function clearAttempts(): void {
  safely(() => {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k !== null && k.startsWith(PREFIX)) keys.push(k)
    }
    for (const k of keys) localStorage.removeItem(k)
  }, undefined)
}

/** Earlier VALID attempts this one may be compared with. */
export function comparableEarlier(all: readonly Attempt[], current: Attempt): Attempt[] {
  return all.filter((a) => a !== current && a.summary.valid && !a.interrupted && comparable(a, current))
}

/** Independent days of valid attempts before a trend is drawn (Canvas 04's learning stage). */
export const LEARNING_STAGE_DAYS = 5

/**
 * Local calendar days, never UTC ones: `at` is a full UTC instant, read here in
 * the browser's zone. An `at` that does not parse adds no day, so corrupt
 * storage cannot invent one.
 */
export function independentDays(attempts: readonly Attempt[]): number {
  const days = new Set<string>()
  for (const a of attempts) {
    const d = new Date(a.at)
    if (!Number.isNaN(d.getTime())) days.add(localIsoDay(d))
  }
  return days.size
}
